// Modal stats panel rendered over the main UI.
//
// Pulls the most recent rounds from `recent_rounds` IPC, joins each
// round's per-pipeline guesses against the truth (when recorded),
// computes haversine distance per pipeline, and aggregates into a
// "last N rounds" rollup table (avg / median distance per pipeline,
// hit rate at 500 km, country match rate).
//
// Distance is computed client-side on the join — keeps the IPC
// returning a single shape (rounds + their guesses) rather than
// pre-aggregated stats, so future filters (per-mode, time range)
// can live in the UI without a roundtrip.

import { useEffect, useMemo, useState } from "react";
import { recentRounds, type GuessRecord, type StoredRound } from "../ipc/commands";
import { useStore } from "../state/store";

interface StatsPanelProps {
  onClose: () => void;
}

interface Rollup {
  pipeline: string;
  attempted: number;
  /// Rounds where the pipeline produced a guess AND the round has
  /// numeric truth_lat/truth_lng recorded. Required for distance
  /// computation; not for country match.
  withCoordsTruth: number;
  /// Rounds where the pipeline produced a guess AND the round has
  /// truth_country recorded (regardless of lat/lng). Country-match
  /// stats use this denominator. The auto-truth path from
  /// score-screen OCR usually populates only truth_country, so
  /// this is the larger pool.
  withCountryTruth: number;
  avgKm: number | null;
  medianKm: number | null;
  hitRate500: number | null;
  /// Fraction of withCountryTruth rounds where the pipeline's
  /// `region` string contained the truth_country name (case-
  /// insensitive). Substring match is intentionally lenient: a
  /// model returning "Rio de Janeiro, Brazil" matches truth_country
  /// "Brazil" without needing a real reverse-geocoder.
  countryMatchRate: number | null;
}

function haversineKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371; // Earth radius in km
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

function rollupByPipeline(rounds: StoredRound[]): Rollup[] {
  const buckets = new Map<string, GuessRecord[]>();
  const coordsTruths = new Map<string, { lat: number; lng: number }>();
  const countryTruths = new Map<string, string>();
  for (const r of rounds) {
    if (r.truth_lat != null && r.truth_lng != null) {
      coordsTruths.set(r.id, { lat: r.truth_lat, lng: r.truth_lng });
    }
    if (r.truth_country) {
      countryTruths.set(r.id, r.truth_country);
    }
    for (const g of r.guesses) {
      if (!buckets.has(g.pipeline)) buckets.set(g.pipeline, []);
      buckets.get(g.pipeline)!.push(g);
    }
  }
  const rollups: Rollup[] = [];
  for (const [pipeline, guesses] of buckets) {
    const distances: number[] = [];
    let withCoordsTruth = 0;
    let withCountryTruth = 0;
    let countryMatches = 0;
    for (const g of guesses) {
      if (g.status !== "complete") continue;
      const coordsTruth =
        g.lat != null && g.lng != null ? coordsTruths.get(g.round_id) : undefined;
      if (coordsTruth) {
        withCoordsTruth += 1;
        distances.push(haversineKm(coordsTruth.lat, coordsTruth.lng, g.lat!, g.lng!));
      }
      const countryTruth = countryTruths.get(g.round_id);
      if (countryTruth && g.region) {
        withCountryTruth += 1;
        // Lenient substring match: "Rio de Janeiro, Brazil" matches
        // truth "Brazil"; "Northern Ireland" matches "Ireland"
        // (intentional false positive — the user can refine the
        // matcher if it bites). Casefold via toLowerCase so case
        // variations don't miss.
        if (
          g.region.toLowerCase().includes(countryTruth.toLowerCase())
        ) {
          countryMatches += 1;
        }
      }
    }
    const avg =
      distances.length === 0
        ? null
        : distances.reduce((sum, d) => sum + d, 0) / distances.length;
    const med = median(distances);
    const hits = distances.filter((d) => d <= 500).length;
    const hitRate = distances.length === 0 ? null : hits / distances.length;
    rollups.push({
      pipeline,
      attempted: guesses.length,
      withCoordsTruth,
      withCountryTruth,
      avgKm: avg,
      medianKm: med,
      hitRate500: hitRate,
      countryMatchRate:
        withCountryTruth === 0 ? null : countryMatches / withCountryTruth,
    });
  }
  // Stable ordering: pinpoint models first, then OCR / meta /
  // reverse_image / final / others.
  const order = [
    "ai_gemini",
    "ai_claude",
    "ai_gpt",
    "final",
    "ocr",
    "meta",
    "reverse_image",
  ];
  rollups.sort((a, b) => {
    const ai = order.indexOf(a.pipeline);
    const bi = order.indexOf(b.pipeline);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  return rollups;
}

function fmtKm(km: number | null): string {
  if (km == null) return "—";
  if (km < 1) return `${(km * 1000).toFixed(0)} m`;
  if (km < 100) return `${km.toFixed(1)} km`;
  return `${km.toFixed(0)} km`;
}

function fmtPct(rate: number | null): string {
  if (rate == null) return "—";
  return `${(rate * 100).toFixed(0)}%`;
}

export function StatsPanel({ onClose }: StatsPanelProps) {
  const [rounds, setRounds] = useState<StoredRound[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const showToast = useStore((s) => s.showToast);

  useEffect(() => {
    let cancelled = false;
    recentRounds(100)
      .then((rs) => {
        if (!cancelled) setRounds(rs);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(String(e));
        showToast(`Stats query failed: ${String(e)}`, "error");
      });
    return () => {
      cancelled = true;
    };
  }, [showToast]);

  const rollups = useMemo(
    () => (rounds ? rollupByPipeline(rounds) : []),
    [rounds],
  );

  const truthCount = rounds?.filter(
    (r) => r.truth_lat != null && r.truth_lng != null,
  ).length;

  return (
    <div
      data-testid="stats-panel"
      className="fixed inset-0 z-50 bg-black p-4 overflow-auto text-fg"
    >
      <div className="flex items-center gap-2 mb-3">
        <h1 className="text-base font-semibold">Stats</h1>
        <span className="text-xs text-fg-muted">
          {rounds == null
            ? "loading…"
            : `${rounds.length} rounds, ${truthCount ?? 0} with truth`}
        </span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onClose}
          className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20"
        >
          Close
        </button>
      </div>
      {error && (
        <div className="text-xs text-[color:var(--err)] mb-2">{error}</div>
      )}
      {rounds && rounds.length === 0 && (
        <div className="text-fg-muted text-xs">
          No rounds recorded yet. Capture a few and use the Truth button on the
          footer to record actual answers — stats will populate here.
        </div>
      )}
      {rollups.length > 0 && (
        <div className="rounded border border-white/10 bg-white/5 p-2">
          <h2 className="text-xs font-semibold mb-2">
            Per-pipeline accuracy across last {rounds?.length} rounds
          </h2>
          <table className="w-full text-xs tabular-nums">
            <thead>
              <tr className="text-fg-muted">
                <th className="text-left py-1 font-normal">Pipeline</th>
                <th className="text-right py-1 font-normal" title="Times this pipeline produced a complete guess">
                  Attempts
                </th>
                <th
                  className="text-right py-1 font-normal"
                  title="Rounds with country truth recorded (auto-truth from score-screen OCR)"
                >
                  w/ country
                </th>
                <th
                  className="text-right py-1 font-normal"
                  title="Pipeline's region string contained the truth country name"
                >
                  Country %
                </th>
                <th
                  className="text-right py-1 font-normal"
                  title="Rounds with numeric lat/lng truth (manual entry only — score screens don't show coords)"
                >
                  w/ coords
                </th>
                <th className="text-right py-1 font-normal">Avg km</th>
                <th className="text-right py-1 font-normal">Median km</th>
                <th className="text-right py-1 font-normal">≤500 km</th>
              </tr>
            </thead>
            <tbody>
              {rollups.map((r) => (
                <tr key={r.pipeline} className="border-t border-white/5">
                  <td className="py-1 font-semibold">{r.pipeline}</td>
                  <td className="py-1 text-right">{r.attempted}</td>
                  <td className="py-1 text-right">{r.withCountryTruth}</td>
                  <td className="py-1 text-right">{fmtPct(r.countryMatchRate)}</td>
                  <td className="py-1 text-right">{r.withCoordsTruth}</td>
                  <td className="py-1 text-right">{fmtKm(r.avgKm)}</td>
                  <td className="py-1 text-right">{fmtKm(r.medianKm)}</td>
                  <td className="py-1 text-right">{fmtPct(r.hitRate500)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
